import { Alert, Anchor, Card, Group, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { usePatientMergeHistory } from '../api/patient-merge';

interface Props {
  patientId: string;
  orgId: string;
}

/**
 * "Merge history" section on the patient detail page: the records absorbed
 * INTO this patient. Renders nothing when there is no history so the page
 * stays clean for the common (never-merged) case.
 */
export function PatientMergeHistory({ patientId, orgId }: Props) {
  const query = usePatientMergeHistory(patientId, orgId);

  if (query.isLoading) {
    return (
      <Card withBorder padding="lg">
        <Stack gap="md">
          <Title order={5}>Merge history</Title>
          <Text c="dimmed">Loading…</Text>
        </Stack>
      </Card>
    );
  }
  if (query.isError) {
    return (
      <Card withBorder padding="lg">
        <Stack gap="md">
          <Title order={5}>Merge history</Title>
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            Could not load merge history.
          </Alert>
        </Stack>
      </Card>
    );
  }

  const records = query.data?.absorbed_records ?? [];
  if (records.length === 0) return null;

  return (
    <Card withBorder padding="lg">
      <Stack gap="md">
        <Title order={5}>Merge history</Title>
        <Stack gap="sm">
          {records.map((r) => (
            <Stack key={r.merge_id} gap={2}>
              <Group gap="xs">
                <Anchor
                  component={Link}
                  to={`/patients/${encodeURIComponent(r.absorbed_patient_id)}`}
                >
                  {r.absorbed_snapshot.given_name} {r.absorbed_snapshot.family_name}
                </Anchor>
                <Text c="dimmed" size="sm">
                  (DOB {r.absorbed_snapshot.dob})
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                Merged {new Date(r.merged_at).toLocaleString()} by {r.actor.display}
              </Text>
              {r.reason && <Text size="sm">Reason: {r.reason}</Text>}
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}
